import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { Column } from '../../components/DataTable';
import Modal from '../../components/Modal';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import NumericInput from '../../components/NumericInput';
import '../../App.css';
import './OrganizationsPage.css';
import { translateError } from '../../utils/errorMessages';
import { unwrapImportFile, isExportEnvelope, classifyImportFile } from '../../utils/importPayload';

interface IOrganization {
  _id: string;
  name: string;
  slug: string;
  subscription: {
    plan: string;
    status: string;
  };
  userCount: number;
  memberCount: number;
  createdAt: string;
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  /** Row count per tenant table — the backend derives all of them, not a subset. */
  stats: Record<string, number>;
}

/** POST /api/super-admin/organizations/import — the full result. */
interface ImportReport {
  success: boolean;
  /** false = the import RAN but lost rows. See `skipped` / `warnings`. */
  complete: boolean;
  organization?: { _id: string | number; name: string; slug: string };
  stats: Record<string, number>;
  /** Rows genuinely LOST — missing people, events, payments. Clears `complete`. */
  skipped: Record<string, number>;
  /**
   * Optional references nulled because their target was not imported. The row
   * IS there; only one link dangles. A different question from `skipped`, so it
   * is counted and shown separately and does NOT clear `complete`.
   */
  nulledLinks?: Record<string, number>;
  errors: string[];
  warnings: string[];
}

/** POST /api/super-admin/demo-orgs — the credentials are shown once, here. */
interface DemoResult {
  organization: { _id: number; name: string; slug: string; demoExpiresAt: string };
  owner: { email: string; password: string };
  profile: { key: string; label: string };
  counts: Record<string, number>;
}

/**
 * The tenant URL for a slug, derived from where super-admin is being served.
 * `admin.eventik.cz` -> `<slug>.eventik.cz`; plain localhost has no subdomain
 * routing, so the dev tenant switcher is the way in there.
 */
const demoTenantUrl = (slug: string): string => {
  const { protocol, hostname, port } = window.location;
  const suffix = port ? `:${port}` : '';
  const labels = hostname.split('.');
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}${suffix} (switch tenant to ${slug})`;
  }
  const apex = labels.length >= 3 ? labels.slice(1).join('.') : hostname;
  return `${protocol}//${slug}.${apex}${suffix}`;
};

/** `eventTemplates` -> `Event templates`. Super-admin diagnostics, untranslated. */
const humanizeTableKey = (key: string): string => {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const OrganizationsPage: React.FC = () => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState<IOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Export state
  const [exportingId, setExportingId] = useState<string | null>(null);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importData, setImportData] = useState<any>(null);
  const [newSlug, setNewSlug] = useState('');
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importSuccess, setImportSuccess] = useState<ImportReport | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // What the operator actually picked — decided at file-load, not after a 400.
  const importFileKind = useMemo(() => classifyImportFile(importData), [importData]);

  // Delete modal state
  const [deleteModalOrg, setDeleteModalOrg] = useState<IOrganization | null>(null);
  const [exportBeforeDelete, setExportBeforeDelete] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  // Prefilled demo state
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoName, setDemoName] = useState('');
  const [demoProfile, setDemoProfile] = useState('tennis');
  const [demoDays, setDemoDays] = useState(7);
  const [isCreatingDemo, setIsCreatingDemo] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  const [demoResult, setDemoResult] = useState<DemoResult | null>(null);

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.superAdmin.organizations.list();
      // API returns { organizations: [...], pagination: {...} }
      setOrganizations(response.data.organizations || []);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Export handler
  const handleExport = async (org: IOrganization) => {
    try {
      setExportingId(org._id);
      const response = await api.superAdmin.organizations.export(org._id);

      // Create blob and trigger download
      // response.data is already a Blob when responseType is 'blob'
      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${org.slug}-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setExportingId(null);
    }
  };

  // File input change handler
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportFile(file);
    setValidationResult(null);
    setImportSuccess(null);

    try {
      const text = await file.text();
      // Handles both a plain export and an archive-delete file (same envelope,
      // nested under `archive`).
      const data = unwrapImportFile(JSON.parse(text));
      if (!data) throw new Error('not an object');
      setImportData(data);

      // Pre-fill slug if available
      if (typeof data.organization?.slug === 'string') {
        setNewSlug(data.organization.slug);
      }
    } catch (err) {
      setError(t('superAdmin.organizations.invalidJsonFile'));
      setImportFile(null);
      setImportData(null);
    }
  };

  // Validate import
  const handleValidate = async () => {
    if (!importData) return;

    try {
      setIsValidating(true);
      const payload = {
        exportData: importData,
        ...(newSlug && newSlug !== importData.organization?.slug ? { newSlug } : {}),
      };
      const response = await api.superAdmin.organizations.validateImport(payload);
      setValidationResult(response.data);
    } catch (err: any) {
      setValidationResult({
        valid: false,
        errors: [translateError(err, t)],
        warnings: [],
        stats: {},
      });
    } finally {
      setIsValidating(false);
    }
  };

  // Import organization
  const handleImport = async () => {
    if (!importData || !validationResult?.valid) return;

    try {
      setIsImporting(true);
      const payload = {
        exportData: importData,
        ...(newSlug && newSlug !== importData.organization?.slug ? { newSlug } : {}),
      };
      const { data: report } = await api.superAdmin.organizations.import(payload) as { data: ImportReport };
      setImportSuccess(report);
      fetchOrganizations();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setIsImporting(false);
    }
  };

  // Reset import modal
  const resetImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportData(null);
    setNewSlug('');
    setValidationResult(null);
    setImportSuccess(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Delete handler with optional archive
  const handleDelete = async () => {
    if (!deleteModalOrg) return;

    try {
      setIsDeleting(true);

      if (exportBeforeDelete) {
        // Use archive-delete endpoint which exports before deleting
        const response = await api.superAdmin.organizations.archiveDelete(deleteModalOrg._id);

        // responseType 'blob' hands back whatever the server sent. Never present
        // an unverified payload as a backup -- the data is already gone.
        const text = await (response.data as Blob).text();
        let archive: unknown = null;
        try {
          archive = (JSON.parse(text) as { archive?: unknown }).archive ?? null;
        } catch {
          archive = null;
        }
        // Must be a real export envelope, not just any object.
        if (!isExportEnvelope(archive)) {
          setError(t('dataSafety.archiveMissing'));
          setDeleteModalOrg(null);
          fetchOrganizations();
          return;
        }

        const url = window.URL.createObjectURL(new Blob([text], { type: 'application/json' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${deleteModalOrg.slug}-archive-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // Regular delete
        await api.superAdmin.organizations.delete(deleteModalOrg._id);
      }

      setDeleteModalOrg(null);
      fetchOrganizations();
    } catch (err: any) {
      // With responseType 'blob' the error body is a Blob, so translateError
      // sees nothing. Parse it back before translating.
      if (err?.response?.data instanceof Blob) {
        try {
          err.response.data = JSON.parse(await err.response.data.text());
        } catch {
          err.response.data = undefined;
        }
      }
      setError(translateError(err, t));
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (org: IOrganization) => {
    setDeleteModalOrg(org);
    setExportBeforeDelete(true);
  };

  const columns: Column<IOrganization>[] = useMemo(() => [
    {
      header: t('common.name'),
      render: (org) => (
        <Link to={`/super-admin/organizations/${org._id}`}>
          {org.name}
        </Link>
      ),
    },
    {
      header: t('superAdmin.organizations.slug'),
      key: 'slug',
    },
    {
      header: t('superAdmin.organizations.memberCount'),
      key: 'memberCount',
    },
    {
      header: t('common.status'),
      render: (org) => (
        <span className={`badge badge-status-${org.subscription?.status || 'active'}`}>
          {t(`superAdmin.status.${org.subscription?.status || 'active'}`)}
        </span>
      ),
    },
    {
      header: t('superAdmin.organizations.userCount'),
      key: 'userCount',
    },
    {
      header: t('superAdmin.organizations.createdAt'),
      render: (org) => new Date(org.createdAt).toLocaleDateString(),
    },
    {
      header: t('common.actions'),
      render: (org) => (
        <div className="grid-actions">
          <button
            onClick={() => handleExport(org)}
            className="button-export"
            disabled={exportingId === org._id}
            title={t('superAdmin.organizations.export')}
            aria-label={`${t('superAdmin.organizations.export')} ${org.name}`}
          >
            {exportingId === org._id ? (
              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
            ) : (
              <i className="fas fa-download" aria-hidden="true"></i>
            )}
          </button>
          <Link
            to={`/super-admin/organizations/${org._id}`}
            className="btn btn--primary btn--sm"
            aria-label={`${t('common.edit')} ${org.name}`}
          >
            <i className="fas fa-edit" aria-hidden="true"></i>
          </Link>
          <button
            onClick={() => openDeleteModal(org)}
            className="btn btn--danger btn--sm"
            title={t('common.delete')}
            aria-label={`${t('common.delete')} ${org.name}`}
          >
            <i className="fas fa-trash" aria-hidden="true"></i>
          </button>
        </div>
      ),
    },
  ], [t, exportingId]);

  const handleCreateDemo = async () => {
    if (!demoName.trim()) return;
    setIsCreatingDemo(true);
    setDemoError(null);
    try {
      const response = await api.superAdmin.demoOrgs.create({
        prospectName: demoName.trim(),
        profile: demoProfile,
        expiresInDays: demoDays,
      });
      setDemoResult(response.data);
      fetchOrganizations();
    } catch (err: any) {
      setDemoError(translateError(err, t));
    } finally {
      setIsCreatingDemo(false);
    }
  };

  const resetDemoModal = () => {
    setShowDemoModal(false);
    setDemoName('');
    setDemoProfile('tennis');
    setDemoDays(7);
    setDemoError(null);
    setDemoResult(null);
  };

  if (loading) return <p>{t('common.loading')}</p>;
  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-list-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>{t('superAdmin.organizations.title')}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button variant="secondary" onClick={() => setShowDemoModal(true)}>
            <i className="fas fa-wand-magic-sparkles" style={{ marginRight: '0.5rem' }}></i>
            {t('superAdmin.demo.createButton')}
          </Button>
          <Button variant="secondary" onClick={() => setShowImportModal(true)}>
            <i className="fas fa-upload" style={{ marginRight: '0.5rem' }}></i>
            {t('superAdmin.organizations.import')}
          </Button>
          <Link to="/super-admin/organizations/create" className="btn btn--primary btn--md">
            {t('superAdmin.organizations.createNew')}
          </Link>
        </div>
      </div>

      {organizations.length === 0 ? (
        <p>{t('superAdmin.organizations.noOrganizations')}</p>
      ) : (
        <table className="data-table" role="table">
          <thead>
            <tr role="row">
              {columns.slice(0, -1).map((col, index) => (
                <th key={index} role="columnheader" scope="col">{col.header}</th>
              ))}
              <th role="columnheader" scope="col">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {organizations.map((org) => (
              <tr key={org._id} role="row">
                {columns.slice(0, -1).map((col, index) => (
                  <td key={index} role="cell">
                    {col.render ? col.render(org) : col.key ? (org as any)[col.key] : null}
                  </td>
                ))}
                <td role="cell">
                  {columns[columns.length - 1].render?.(org)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Import Modal */}
      <Modal
        isOpen={showDemoModal}
        onClose={resetDemoModal}
        title={t('superAdmin.demo.createTitle')}
        size="medium"
        dismissible={!isCreatingDemo}
      >
        {demoResult ? (
          <div>
            <p>{t('superAdmin.demo.ready', { name: demoResult.organization.name })}</p>
            <dl className="demo-modal__result">
              <dt>{t('superAdmin.demo.url')}</dt>
              <dd><code>{demoTenantUrl(demoResult.organization.slug)}</code></dd>
              <dt>{t('superAdmin.demo.login')}</dt>
              <dd><code>{demoResult.owner.email}</code></dd>
              <dt>{t('superAdmin.demo.password')}</dt>
              <dd><code>{demoResult.owner.password}</code></dd>
              <dt>{t('superAdmin.demo.expires')}</dt>
              <dd>{new Date(demoResult.organization.demoExpiresAt).toLocaleDateString()}</dd>
              <dt>{t('superAdmin.demo.contents')}</dt>
              <dd>
                {Object.entries(demoResult.counts)
                  .map(([key, value]) => `${humanizeTableKey(key)}: ${value}`)
                  .join(' · ')}
              </dd>
            </dl>
            <p className="demo-modal__warning">
              <i className="fas fa-triangle-exclamation" aria-hidden="true"></i>{' '}
              {t('superAdmin.demo.passwordOnce')}
            </p>
            <div className="demo-modal__actions">
              <Button variant="primary" onClick={resetDemoModal}>{t('common.close')}</Button>
            </div>
          </div>
        ) : (
          <div>
            <p>{t('superAdmin.demo.createHelp')}</p>
            <FormField
              label={t('superAdmin.demo.prospectName')}
              htmlFor="demo-name"
              required
              helperText={t('superAdmin.demo.prospectNameHelp')}
            >
              <input
                id="demo-name"
                type="text"
                className="form-input"
                value={demoName}
                onChange={(e) => setDemoName(e.target.value)}
                placeholder="RC Baráček"
                disabled={isCreatingDemo}
              />
            </FormField>
            <FormField label={t('superAdmin.demo.profile')} htmlFor="demo-profile">
              <select
                id="demo-profile"
                className="form-input"
                value={demoProfile}
                onChange={(e) => setDemoProfile(e.target.value)}
                disabled={isCreatingDemo}
              >
                <option value="tennis">{t('superAdmin.demo.profileTennis')}</option>
                <option value="school">{t('superAdmin.demo.profileSchool')}</option>
              </select>
            </FormField>
            <FormField
              label={t('superAdmin.demo.expiresInDays')}
              htmlFor="demo-days"
              helperText={t('superAdmin.demo.expiresInDaysHelp')}
            >
              <NumericInput
                id="demo-days"
                value={demoDays}
                onChange={(e) => setDemoDays(Number(e.target.value) || 7)}
                min={1}
                max={365}
                disabled={isCreatingDemo}
              />
            </FormField>
            {demoError && <p className="error-message">{demoError}</p>}
            <div className="demo-modal__actions">
              <Button variant="secondary" onClick={resetDemoModal} disabled={isCreatingDemo}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateDemo}
                disabled={isCreatingDemo || !demoName.trim()}
              >
                {isCreatingDemo ? t('superAdmin.demo.creating') : t('superAdmin.demo.create')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showImportModal}
        onClose={resetImportModal}
        title={t('superAdmin.organizations.importTitle')}
        size="medium"
      >
        <div className="import-modal">
          {importSuccess ? (
            <div className="import-modal__success">
              <div className="import-modal__success-icon">
                <i className={`fas ${importSuccess.complete ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
              </div>
              <h3>
                {importSuccess.complete
                  ? t('superAdmin.organizations.importSuccess')
                  : t('dataSafety.importIncomplete')}
              </h3>
              <p>
                {t('superAdmin.organizations.importSuccessMessage', {
                  name: importSuccess.organization?.name ?? '',
                })}
              </p>

              {/* A lossy import must never look like a clean one. */}
              {!importSuccess.complete && (
                <p className="error-message">
                  {t('dataSafety.importIncompleteMessage', {
                    count: Object.values(importSuccess.skipped ?? {}).reduce((a, b) => a + b, 0),
                  })}
                </p>
              )}

              {Object.keys(importSuccess.skipped ?? {}).length > 0 && (
                <div className="import-modal__validation-errors">
                  <strong>{t('dataSafety.importSkippedTitle')}:</strong>
                  <ul>
                    {Object.entries(importSuccess.skipped)
                      .sort((a, b) => b[1] - a[1])
                      .map(([key, n]) => (
                        <li key={key}>{humanizeTableKey(key)}: {n}</li>
                      ))}
                  </ul>
                </div>
              )}

              {/* Rows that ARE there with one dangling optional reference —
                  a different question from a missing row, so never summed in. */}
              {Object.keys(importSuccess.nulledLinks ?? {}).length > 0 && (
                <div className="import-modal__validation-warnings">
                  <strong>{t('dataSafety.importNulledLinksTitle')}:</strong>
                  <p className="import-modal__stats-note">
                    {t('dataSafety.importNulledLinksNote')}
                  </p>
                  <ul>
                    {Object.entries(importSuccess.nulledLinks ?? {})
                      .sort((a, b) => b[1] - a[1])
                      .map(([key, n]) => (
                        <li key={key}>{humanizeTableKey(key)}: {n}</li>
                      ))}
                  </ul>
                </div>
              )}

              {(importSuccess.warnings?.length ?? 0) > 0 && (
                <div className="import-modal__validation-warnings">
                  <strong>{t('superAdmin.organizations.warnings')}:</strong>
                  <ul>
                    {importSuccess.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="import-modal__actions">
                <Button variant="secondary" onClick={resetImportModal}>
                  {t('common.cancel')}
                </Button>
                {importSuccess.organization && (
                  <Link
                    to={`/super-admin/organizations/${importSuccess.organization._id}`}
                    className="btn btn--primary btn--md"
                    onClick={resetImportModal}
                  >
                    {t('superAdmin.organizations.viewOrganization')}
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* File Upload */}
              <div className="import-modal__section">
                <label className="import-modal__label">
                  {t('superAdmin.organizations.selectFile')}
                </label>
                <div className="import-modal__file-input">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleFileChange}
                    id="import-file"
                  />
                  <label htmlFor="import-file" className="import-modal__file-button">
                    <i className="fas fa-file-upload"></i>
                    {importFile ? importFile.name : t('superAdmin.organizations.chooseFile')}
                  </label>
                </div>
              </div>

              {/* Slug Override */}
              {importData && (
                <div className="import-modal__section">
                  <label className="import-modal__label" htmlFor="new-slug">
                    {t('superAdmin.organizations.newSlug')}
                  </label>
                  <input
                    type="text"
                    id="new-slug"
                    value={newSlug}
                    onChange={(e) => setNewSlug(e.target.value)}
                    placeholder={importData.organization?.slug || t('superAdmin.organizations.enterSlug')}
                    className="import-modal__input"
                  />
                  <p className="import-modal__help">
                    {t('superAdmin.organizations.newSlugHelp')}
                  </p>
                </div>
              )}

              {/* What this file actually is — a scoped report is not a backup. */}
              {importFileKind && !importFileKind.restorable && (
                <div className="import-modal__validation import-modal__validation--invalid">
                  <div className="import-modal__validation-header">
                    <i className="fas fa-times-circle"></i>
                    <span>{t('dataSafety.fileNotRestorable', { kind: importFileKind.kind })}</span>
                  </div>
                  {importFileKind.omittedTables.length > 0 && (
                    <p className="import-modal__stats-note">
                      {t('dataSafety.fileOmittedTables', {
                        tables: importFileKind.omittedTables.map(humanizeTableKey).join(', '),
                      })}
                    </p>
                  )}
                </div>
              )}

              {/* Absent sections are not empty sections. */}
              {importFileKind && importFileKind.restorable && importFileKind.missingTables.length > 0 && (
                <p className="import-modal__stats-note">
                  {t('dataSafety.fileMissingTables', {
                    tables: importFileKind.missingTables.map(humanizeTableKey).join(', '),
                  })}
                </p>
              )}

              {/* Validation Results */}
              {validationResult && (
                <div className={`import-modal__validation ${validationResult.valid ? 'import-modal__validation--valid' : 'import-modal__validation--invalid'}`}>
                  <div className="import-modal__validation-header">
                    {validationResult.valid ? (
                      <>
                        <i className="fas fa-check-circle"></i>
                        <span>{t('superAdmin.organizations.validationPassed')}</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-times-circle"></i>
                        <span>{t('superAdmin.organizations.validationFailed')}</span>
                      </>
                    )}
                  </div>

                  {/* Errors */}
                  {validationResult.errors.length > 0 && (
                    <div className="import-modal__validation-errors">
                      <strong>{t('superAdmin.organizations.errors')}:</strong>
                      <ul>
                        {validationResult.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Warnings */}
                  {validationResult.warnings.length > 0 && (
                    <div className="import-modal__validation-warnings">
                      <strong>{t('superAdmin.organizations.warnings')}:</strong>
                      <ul>
                        {validationResult.warnings.map((warn, i) => (
                          <li key={i}>{warn}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Stats — every tenant table the backend counted, so an
                      operator sees memberships/payments/seasons before committing. */}
                  {validationResult.valid && (
                    <div className="import-modal__validation-stats">
                      <strong>{t('superAdmin.organizations.importStats')}:</strong>
                      <ul>
                        {Object.entries(validationResult.stats)
                          .filter(([, n]) => n > 0)
                          .sort((a, b) => b[1] - a[1])
                          .map(([key, n]) => (
                            <li key={key}>{humanizeTableKey(key)}: {n}</li>
                          ))}
                      </ul>
                      <p className="import-modal__stats-note">
                        {t('dataSafety.statsEmptySections', {
                          count: Object.values(validationResult.stats).filter((n) => n === 0).length,
                        })}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="import-modal__actions">
                <Button variant="secondary" onClick={resetImportModal}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleValidate}
                  disabled={!importData || !importFileKind?.restorable || isValidating}
                  loading={isValidating}
                >
                  {t('superAdmin.organizations.validate')}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleImport}
                  disabled={!validationResult?.valid || !importFileKind?.restorable || isImporting}
                  loading={isImporting}
                >
                  {t('superAdmin.organizations.importButton')}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteModalOrg}
        onClose={() => setDeleteModalOrg(null)}
        title={t('superAdmin.organizations.deleteTitle')}
        size="small"
      >
        <div className="delete-modal">
          <p className="delete-modal__message">
            {t('superAdmin.organizations.confirmDelete')}
          </p>
          <p className="delete-modal__org-name">
            <strong>{deleteModalOrg?.name}</strong> ({deleteModalOrg?.slug})
          </p>

          <label className="delete-modal__checkbox">
            <input
              type="checkbox"
              checked={exportBeforeDelete}
              onChange={(e) => setExportBeforeDelete(e.target.checked)}
            />
            <span>{t('superAdmin.organizations.exportBeforeDelete')}</span>
          </label>
          <p className="delete-modal__checkbox-help">
            {t('superAdmin.organizations.exportBeforeDeleteHelp')}
          </p>

          <div className="delete-modal__actions">
            <Button variant="secondary" onClick={() => setDeleteModalOrg(null)} disabled={isDeleting}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={isDeleting}>
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizationsPage;
